import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Save, RefreshCw, PlayCircle, Send, BellRing } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Switch } from "../components/ui/switch";

function pollError(error) {
  if (error.response?.data?.detail) return formatApiError(error.response.data.detail);
  if ([502, 503, 504].includes(error.response?.status)) return "The server did not respond in time. Check the polling status before retrying.";
  return "Cannot contact the server. Check your connection and refresh the polling status.";
}

export default function EmailConfigPage() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTestTo, setSmtpTestTo] = useState("");
  const [recipientsText, setRecipientsText] = useState("");
  const [pollState, setPollState] = useState({});
  const [extraTime, setExtraTime] = useState("05:00");
  const [statusError, setStatusError] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/email-config");
    setCfg({ ...data, imap_password: "" });
    setPollState(data);
    setRecipientsText((data.notify_emails || []).join(", "));
  }, []);
  useEffect(() => {
    load().catch((error) => toast.error(pollError(error)));
  }, [load]);
  useEffect(() => {
    const timer = setInterval(() => {
      api.get("/email-config").then(({ data }) => {
        setPollState(data);
        setStatusError("");
      }).catch((error) => setStatusError(pollError(error)));
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  if (!cfg) return <div className="pg-card p-10 text-center text-slate-400">Loading...</div>;

  const upd = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e?.target?.value ?? e;
    setCfg((c) => ({ ...c, [k]: v }));
  };

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const payload = { ...cfg };
      if (!payload.imap_password) delete payload.imap_password;
      // Parse comma/newline-separated recipients into an array
      payload.notify_emails = recipientsText
        .split(/[,;\n]/)
        .map((x) => x.trim())
        .filter(Boolean);
      await api.patch("/email-config", payload);
      toast.success("Email config saved");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    toast.loading("Testing IMAP...", { id: "im" });
    try {
      const { data } = await api.post("/email-config/test");
      toast.dismiss("im");
      if (data.ok) toast.success(data.message);
      else toast.error(data.message);
    } catch (e) {
      toast.dismiss("im");
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  const pollNow = async () => {
    setPolling(true);
    toast.loading("Polling inbox...", { id: "pl" });
    try {
      const { data } = await api.post("/email-config/poll-now");
      toast.dismiss("pl");
      if (data.ok === false) toast.warning(data.message || "The inbox poll could not start.");
      else if (data.status === "started") {
        toast.success(data.message);
        setPollState((current) => ({ ...current, poll_running: true, poll_status: "running", last_poll_started_at: new Date().toISOString() }));
      } else {
        const s = data.summary || {};
        if (s.errors?.length) toast.error(s.errors.join("; "));
        else toast.success(`Checked: ${s.checked || 0} · Processed: ${s.processed || 0} · Skipped: ${s.skipped || 0}`);
      }
    } catch (e) {
      toast.dismiss("pl");
      toast.error(pollError(e));
    } finally {
      setPolling(false);
    }
  };

  const testSmtp = async () => {
    const to = (smtpTestTo || "").trim();
    if (!to) {
      toast.error("Enter a recipient email first");
      return;
    }
    setSmtpTesting(true);
    toast.loading("Sending test email...", { id: "sm" });
    try {
      const { data } = await api.post("/email-config/test-smtp", { to });
      toast.dismiss("sm");
      if (data.ok) toast.success(data.message);
      else toast.error(data.message);
    } catch (e) {
      toast.dismiss("sm");
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSmtpTesting(false);
    }
  };

  return (
    <div data-testid="email-config-page" className="max-w-4xl">
      <div className="mb-7">
        <div className="text-xs uppercase tracking-[0.32em] text-slate-400 mb-1">
          Settings · Email Config
        </div>
        <h1 className="text-3xl font-extrabold text-slate-900">IMAP Email Polling</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-2xl">
          POS2GTO checks this inbox every few minutes for new sales emails. Each email is routed to
          the correct store by extracting the Machine ID from the recipient's plus-address
          (e.g. <span className="mono text-slate-700">gto+6000459@pos2gto.site</span>).
        </p>
      </div>

      <form onSubmit={save} className="space-y-6">
        <section className="pg-card p-6 lg:p-8">
          <div className="field-section-title mb-5">
            <Mail size={16} /> Connection
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="IMAP Host">
              <input className={inp} value={cfg.imap_host || ""} onChange={upd("imap_host")} placeholder="imap.example.com" data-testid="ec-host" />
            </Field>
            <Field label="Port">
              <input className={inp} type="number" value={cfg.imap_port || 993} onChange={upd("imap_port")} />
            </Field>
            <Field label="Use SSL">
              <select className={inp} value={String(cfg.use_ssl ?? true)} onChange={(e) => setCfg((c) => ({ ...c, use_ssl: e.target.value === "true" }))}>
                <option value="true">Yes (recommended)</option>
                <option value="false">No</option>
              </select>
            </Field>
            <Field label="Username (full email)">
              <input className={inp} value={cfg.imap_username || ""} onChange={upd("imap_username")} placeholder="gto@pos2gto.site" data-testid="ec-user" />
            </Field>
            <Field label={cfg.imap_password_set ? "Password (leave empty to keep)" : "Password"}>
              <input className={inp} type="password" value={cfg.imap_password || ""} onChange={upd("imap_password")} placeholder={cfg.imap_password_set ? "••••••••" : ""} data-testid="ec-pass" />
            </Field>
            <Field label="Mailbox">
              <input className={inp} value={cfg.mailbox || "INBOX"} onChange={upd("mailbox")} />
            </Field>
            <Field label="Plus-address local part">
              <input className={inp} value={cfg.plus_address_local || "gto"} onChange={upd("plus_address_local")} placeholder="gto" />
              <div className="text-[11px] text-slate-400 mt-1">
                The text before the '+' in <span className="mono">gto+MACHINE@domain</span>
              </div>
            </Field>
            <Field label="Polling interval (minutes)">
              <input className={inp} type="number" min="1" max="1440" value={cfg.polling_interval_minutes || 5} onChange={upd("polling_interval_minutes")} />
            </Field>
            <Field label="Enabled">
              <div className="flex items-center h-[42px] gap-3">
                <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setCfg((c) => ({ ...c, enabled: v }))} data-testid="ec-enabled" />
                <span className="text-sm text-slate-600">{cfg.enabled ? "Polling active" : "Polling disabled"}</span>
              </div>
            </Field>
          </div>
        </section>

        <section className="pg-card p-6 lg:p-8">
          <div className="field-section-title mb-3">Polling schedule · Singapore time (UTC+08:00)</div>
          <p className="text-sm text-slate-500 mb-5">Save config to apply your schedule. Poll now works at any time. Disabling polling stops all automatic runs.</p>
          <div className="flex items-center gap-3 mb-4">
            <Switch checked={cfg.interval_polling_enabled ?? true} onCheckedChange={(value) => setCfg((c) => ({ ...c, interval_polling_enabled: value }))} />
            <span className="text-sm">Run repeatedly at the configured interval</span>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <Switch checked={cfg.polling_window_enabled ?? false} onCheckedChange={(value) => setCfg((c) => ({ ...c, polling_window_enabled: value }))} />
            <span className="text-sm">Limit repeating polls to a daily time window</span>
          </div>
          {cfg.polling_window_enabled && (
            <div className="grid grid-cols-2 gap-4 mb-5">
              <Field label="From (inclusive)"><input type="time" required className={inp} value={cfg.polling_window_start || "18:00"} onChange={upd("polling_window_start")} /></Field>
              <Field label="Until (exclusive)"><input type="time" required className={inp} value={cfg.polling_window_end || "01:00"} onChange={upd("polling_window_end")} /></Field>
              <p className="text-xs text-slate-500 col-span-2">Windows may cross midnight: 18:00–01:00 means 6 pm until 1 am the following morning. A poll already in progress finishes normally.</p>
            </div>
          )}
          <Field label="Additional daily polls">
            <div className="flex gap-2 items-center">
              <input type="time" aria-label="Additional poll time" className={`${inp} max-w-[180px]`} value={extraTime} onChange={(e) => setExtraTime(e.target.value)} />
              <button type="button" className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm" disabled={!extraTime || (cfg.scheduled_poll_times || []).length >= 48} onClick={() => setCfg((c) => ({ ...c, scheduled_poll_times: [...new Set([...(c.scheduled_poll_times || []), extraTime])].sort() }))}>Add time</button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {(cfg.scheduled_poll_times || []).map((time) => (
                <span key={time} className="inline-flex items-center gap-3 rounded-lg bg-blue-50 text-blue-800 px-3 py-2 text-sm">
                  {time}<button type="button" aria-label={`Remove ${time}`} onClick={() => setCfg((c) => ({ ...c, scheduled_poll_times: c.scheduled_poll_times.filter((value) => value !== time) }))}>×</button>
                </span>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">These times also run outside the repeating window. If another poll is busy, the scheduled scan runs once it finishes (up to 24 hours later).</p>
          </Field>
        </section>

        <section className="pg-card p-6 lg:p-8 bg-slate-50/50">
          {statusError && <p className="text-sm text-rose-600 mb-4">Status could not be refreshed: {statusError}</p>}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <Info label="Last completed (Singapore)" value={pollState.last_polled_at ? new Date(pollState.last_polled_at).toLocaleString("en-GB", { timeZone: "Asia/Singapore" }) : "Never"} />
            <Info label="Last error" value={pollState.last_error || "—"} tone={pollState.last_error ? "rose" : ""} />
            <Info label="Status" value={pollState.poll_running ? "Running" : pollState.poll_status === "interrupted" ? "Interrupted — ready to retry" : pollState.poll_status === "error" ? "Last poll failed — see error" : "Idle"} tone={pollState.poll_running ? "green" : "slate"} />
            <Info label="Last started (Singapore)" value={pollState.last_poll_started_at ? new Date(pollState.last_poll_started_at).toLocaleString("en-GB", { timeZone: "Asia/Singapore" }) : "—"} />
            <Info label="Last trigger" value={pollState.last_poll_trigger || "—"} />
            <Info label="Last completed result" value={pollState.last_poll_summary ? `Checked: ${pollState.last_poll_summary.checked || 0} · Processed: ${pollState.last_poll_summary.processed || 0} · Skipped: ${pollState.last_poll_summary.skipped || 0}` : "—"} />
          </div>
        </section>

        <section className="pg-card p-6 lg:p-8">
          <div className="field-section-title mb-2 flex items-center gap-2">
            <BellRing size={16} /> Error notifications (outgoing email)
          </div>
          <p className="text-sm text-slate-500 mb-5 max-w-3xl">
            When a file enters the error log after the 3 automatic retries, an email is sent to the recipients below.
            Uses the same IMAP credentials for SMTP — Gmail App Passwords work for both.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="SMTP Host">
              <input className={inp} value={cfg.smtp_host || ""} onChange={upd("smtp_host")} placeholder="smtp.gmail.com" data-testid="ec-smtp-host" />
            </Field>
            <Field label="Port">
              <input className={inp} type="number" value={cfg.smtp_port ?? 587} onChange={upd("smtp_port")} data-testid="ec-smtp-port" />
            </Field>
            <Field label="Use STARTTLS">
              <select
                className={inp}
                value={String(cfg.smtp_use_tls ?? true)}
                onChange={(e) => setCfg((c) => ({ ...c, smtp_use_tls: e.target.value === "true" }))}
                data-testid="ec-smtp-tls"
              >
                <option value="true">Yes (port 587)</option>
                <option value="false">No (port 465 SSL)</option>
              </select>
            </Field>
            <Field label="From address (optional — defaults to IMAP username)">
              <input className={inp} value={cfg.smtp_from_email || ""} onChange={upd("smtp_from_email")} placeholder="POS2GTO <gto@pos2gto.site>" data-testid="ec-smtp-from" />
            </Field>
            <Field label="Notify on error">
              <div className="flex items-center h-[42px] gap-3">
                <Switch
                  checked={!!cfg.notify_on_error}
                  onCheckedChange={(v) => setCfg((c) => ({ ...c, notify_on_error: v }))}
                  data-testid="ec-notify-on-error"
                />
                <span className="text-sm text-slate-600">{cfg.notify_on_error ? "Enabled" : "Disabled"}</span>
              </div>
            </Field>
            <div className="md:col-span-3">
              <Field label="Recipients (comma or new-line separated)">
                <textarea
                  className={`${inp} min-h-[72px] font-mono text-[13px]`}
                  value={recipientsText}
                  onChange={(e) => setRecipientsText(e.target.value)}
                  placeholder="admin@example.com, ops@example.com"
                  data-testid="ec-notify-emails"
                />
              </Field>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[240px]">
              <label className="field-label">Send test to</label>
              <input
                className={inp}
                type="email"
                value={smtpTestTo}
                onChange={(e) => setSmtpTestTo(e.target.value)}
                placeholder="you@example.com"
                data-testid="ec-smtp-test-to"
              />
            </div>
            <button
              type="button"
              onClick={testSmtp}
              disabled={smtpTesting}
              className="px-4 py-2.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-semibold text-sm flex items-center gap-2 disabled:opacity-60"
              data-testid="ec-smtp-test-btn"
            >
              <Send size={15} /> {smtpTesting ? "Sending..." : "Send test email"}
            </button>
          </div>
        </section>

        <div className="flex flex-wrap justify-end gap-3">
          <button type="button" onClick={test} disabled={testing} className="px-4 py-2.5 rounded-lg border border-cyan-200 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 font-semibold text-sm flex items-center gap-2 disabled:opacity-60" data-testid="test-imap-btn">
            <RefreshCw size={15} /> {testing ? "Testing..." : "Test connection"}
          </button>
          <button type="button" onClick={pollNow} disabled={polling || pollState.poll_running} className="px-4 py-2.5 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 font-semibold text-sm flex items-center gap-2 disabled:opacity-60" data-testid="poll-now-btn">
            <PlayCircle size={15} /> {polling || pollState.poll_running ? "Polling..." : "Poll now"}
          </button>
          <button type="submit" disabled={saving} className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-60" data-testid="save-imap-btn">
            <Save size={15} /> {saving ? "Saving..." : "Save config"}
          </button>
        </div>
      </form>
    </div>
  );
}

const inp = "w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none transition bg-white text-sm";

function Field({ label, children }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      {children}
    </div>
  );
}
function Info({ label, value, tone }) {
  const tones = { green: "text-emerald-700", rose: "text-rose-600", slate: "text-slate-500" };
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">{label}</div>
      <div className={`mt-1 font-medium ${tones[tone] || "text-slate-800"} break-words`}>{value}</div>
    </div>
  );
}
